import { ArrowRightOutlined, BellOutlined, SettingOutlined } from '@ant-design/icons'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { Button, Card, Flex, List, theme, Typography } from 'antd'
import React from 'react'
import { Container } from '../components/layout/container'
import { PageHeader } from '../components/page-header'
import { useTranslation } from 'react-i18next'
import { UserAvatar } from '../user/components/user-avatar'
import { PortaledBreadcrumb } from '../components/portaled-breadcrumb'
import { NetworkErrors } from '../components/network-errors'
import { useResendVerification, useUser } from '../user/hooks'
import { UserBlockButton } from '../user/components/buttons/user-block-button'
import { UserForgotButton } from '../user/components/buttons/user-forgot-button'
import { BlockedTag } from '../user/components/blocked-tag'
import { RoleTag } from '../user/components/role-tag'
import { useTitle } from '../hooks/use-title'
import { useActivities } from '../activity/hooks'
import { TimeAgo } from '../components/time-ago'
import { UserImpersonateButton } from '../user/components/buttons/user-impersonate-button'
import { SHOW_USER } from '../user/permission'

export const Route = createFileRoute('/_auth/_main/users/$userId/')({
    beforeLoad: ({ context }) => {
        if (!context.auth.can(SHOW_USER)) {
            throw redirect({
                to: '/dashboard',
            })
        }
    },
    component: ShowUser,
})

function ShowUser() {
    const { t } = useTranslation()
    const { userId } = Route.useParams()
    const query = useUser(userId)
    const { token } = theme.useToken()

    const mutation = useResendVerification(userId)
    const activitiesQuery = useActivities({ query: { filters: { userId: [userId] }, sort: '-created_at', size: 5 } })

    useTitle(t('User'))

    if (query.isPending || query.isError) {
        return (
            <NetworkErrors
                query={query}
                result404={{
                    title: t('User not found'),
                    extra: (
                        <Link to="/users">
                            <Button type="primary">{t('Users')}</Button>
                        </Link>
                    ),
                }}
            />
        )
    }

    const activities = activitiesQuery.data?.data || []

    return (
        <>
            <PortaledBreadcrumb
                items={[
                    {
                        title: <Link to="/users">{t('Users')}</Link>,
                    },
                    {
                        title: query.data.name,
                    },
                ]}
            />
            <PageHeader
                prefix={<UserAvatar user={query.data} />}
                title={
                    <div className="flex items-center gap-4">
                        {query.data.name} {query.data.blockedAt && <BlockedTag className="m-0" />}
                        <RoleTag
                            className="m-0"
                            role={query.data.role}
                        />
                    </div>
                }
            />
            <Container className="py-6">
                <Flex
                    align="start"
                    gap="middle"
                    className="flex-col-reverse md:flex-row"
                >
                    <Card className="shadow-xs w-full lg:w-5/12">
                        <Flex justify="space-between">
                            <Flex
                                gap="small"
                                style={{ marginBottom: token.marginSM }}
                            >
                                <BellOutlined className="text-xl" />
                                <Typography.Title
                                    className="mb-0"
                                    level={5}
                                >
                                    {t('Activities')}
                                </Typography.Title>
                            </Flex>

                            <Link
                                to="/campaigns"
                                disabled
                            >
                                {t('View all')} <ArrowRightOutlined />
                            </Link>
                        </Flex>
                        <List
                            itemLayout="horizontal"
                            dataSource={activities.map((activity) => {
                                return {
                                    title: t(activity.event),
                                    createdAt: activity.createdAt,
                                }
                            })}
                            renderItem={(item) => (
                                <List.Item>
                                    <List.Item.Meta
                                        title={item.title}
                                        description={<TimeAgo date={item.createdAt} />}
                                    />
                                </List.Item>
                            )}
                        />
                    </Card>
                    <Flex
                        vertical
                        className="w-full lg:w-7/12"
                        gap="middle"
                    >
                        <Card className="shadow-xs">
                            <Flex
                                gap="small"
                                style={{ marginBottom: token.marginSM }}
                            >
                                <SettingOutlined className="text-xl" />
                                <Typography.Title
                                    className="mb-0"
                                    level={5}
                                >
                                    {t('Options')}
                                </Typography.Title>
                            </Flex>
                            <Flex
                                gap="middle"
                                vertical
                            >
                                <Flex
                                    gap="middle"
                                    className="flex-col sm:flex-row"
                                >
                                    <UserBlockButton user={query.data} />
                                    <UserForgotButton user={query.data} />
                                </Flex>
                                <Flex
                                    gap="middle"
                                    className="flex-col sm:flex-row"
                                >
                                    <Link
                                        to="/users/$userId/edit"
                                        params={{ userId }}
                                        className="sm:w-auto w-full"
                                    >
                                        <Button block>{t('Edit')}</Button>
                                    </Link>
                                    <UserImpersonateButton user={query.data} />
                                    {!query.data.emailVerifiedAt && (
                                        <Button
                                            loading={mutation.isPending}
                                            onClick={() => mutation.mutate()}
                                        >
                                            {t('Resend verification')}
                                        </Button>
                                    )}
                                </Flex>
                            </Flex>
                        </Card>
                    </Flex>
                </Flex>
            </Container>
        </>
    )
}
